/**
 * Truss analysis: print-friendly HTML (browser print / PDF), all result-tab tables/notes.
 * K-joint debug PNGs are omitted from print (large payloads / slow).
 */

import {
  buildPrintDocumentHtml,
  openPrintHtmlDocument,
  printSectionHtml,
  escapeHtml,
} from '../../utils/printReportShared';
import {
  buildBasisRows,
  buildBottomSegmentRows,
  buildFrameZoneRows,
  buildLoadingsSvgString,
  buildRoofSegmentRows,
  buildRoofZoneSegmentRows,
  buildSnowSegmentRows,
  buildWindCaseRows,
  buildWindZonesTableRows,
  formatGoverningCombination,
  resolveLoadingCases,
} from './trussLoadingsShared';

const FILLET_STRUCT_MIN_MM = 3;

function fmtNum(v, d = 2) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : '—';
}

function utilPercent(u) {
  const x = Number(u) || 0;
  if (!Number.isFinite(x) || x > 1e6) return '—';
  return `${(x * 100).toFixed(1)}%`;
}

function formatProfile(p) {
  if (!p || (p.name == null && p.type == null)) return '—';
  const t = p.type || '';
  const n = p.name || '';
  return [t, n].filter(Boolean).join(' ');
}

function profilesSummary(sp, geometry) {
  if (!sp || typeof sp !== 'object') return [];
  const rows = [];
  rows.push({ label: 'Bottom chord', value: formatProfile(sp.bottom_chord) });
  const tl = sp.top_chord_left;
  const tr = sp.top_chord_right;
  const tc = sp.top_chord;
  if (formatProfile(tl) !== '—' || formatProfile(tr) !== '—') {
    if (formatProfile(tl) === formatProfile(tr)) {
      rows.push({ label: 'Top chord (left & right)', value: formatProfile(tl) || formatProfile(tc) });
    } else {
      rows.push({ label: 'Top chord (left)', value: formatProfile(tl) || formatProfile(tc) });
      rows.push({ label: 'Top chord (right)', value: formatProfile(tr) || formatProfile(tc) });
    }
  } else {
    rows.push({ label: 'Top chord', value: formatProfile(tc) });
  }
  const hasVerticalMembers =
    Array.isArray(geometry?.members) && geometry.members.some((m) => m.group === 'vertical');
  if (hasVerticalMembers) {
    rows.push({ label: 'Verticals', value: formatProfile(sp.vertical) });
  }
  const dg = sp.diagonal_groups;
  if (Array.isArray(dg) && dg.length) {
    dg.forEach((d) => {
      const idx = d.group_index != null ? d.group_index : rows.length;
      rows.push({ label: `Diagonals (group ${idx})`, value: formatProfile(d) });
    });
  }
  if (sp.column) {
    rows.push({ label: 'Portal column', value: formatProfile(sp.column) });
  }
  return rows;
}

function formatWeldChordGapMm(w) {
  if (w.overlap_gap_kind && w.overlap_gap_abs_mm != null) {
    const v = Number(w.overlap_gap_abs_mm).toFixed(1);
    if (w.overlap_gap_kind === 'gap') {
      return `gap ${v} mm`;
    }
    return `overlap ${v} mm`;
  }
  if (w.gap_m != null) {
    const isOverlap = Number(w.gap_m) < 0;
    const v = (Math.abs(Number(w.gap_m)) * 1000).toFixed(1);
    return isOverlap ? `overlap ${v} mm` : `gap ${v} mm`;
  }
  return '—';
}

function formatLoadVariantLabel(s) {
  if (s == null || s === '') return '—';
  return String(s).replace(/_/g, ' ');
}

function displayThroatAMm(w) {
  const a = Number(w.fillet_a_prov_mm);
  if (!Number.isFinite(a)) return { text: '—', clamped: false };
  if (a < FILLET_STRUCT_MIN_MM - 1e-9) return { text: FILLET_STRUCT_MIN_MM.toFixed(0), clamped: true };
  return { text: a.toFixed(2), clamped: false };
}

function _finiteLeffM(raw) {
  if (raw == null) return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

function formatWeldLeffColumnM(w) {
  const fb = _finiteLeffM(w.fillet_L_eff_m);
  const d1raw = _finiteLeffM(w.fillet_L_eff_m_diag1);
  const d2raw = _finiteLeffM(w.fillet_L_eff_m_diag2);
  const n1 = Number.isFinite(d1raw) ? d1raw : fb;
  const n2 = Number.isFinite(d2raw) ? d2raw : fb;
  const has1 = Number.isFinite(n1);
  const has2 = Number.isFinite(n2);
  if (!has1 && !has2) return '—';
  if (has1 && !has2) return n1.toFixed(3);
  if (!has1 && has2) return n2.toFixed(3);
  if (Math.abs(n1 - n2) < 5e-4) return n1.toFixed(3);
  return `d1: ${n1.toFixed(3)}, d2: ${n2.toFixed(3)}`;
}

function formatWeldThroatColumnMm(w) {
  const d1 = w.fillet_a_prov_mm_diag1 ?? w.fillet_a_prov_mm;
  const d2 = w.fillet_a_prov_mm_diag2 ?? w.fillet_a_prov_mm;
  const vv = w.fillet_a_prov_mm_vertical;
  const n1 = Number(d1);
  const n2 = Number(d2);
  const nv = vv != null ? Number(vv) : NaN;
  const eps = 0.05;
  if (!Number.isFinite(n1)) return { text: '—', clamped: false };
  const same12 = Number.isFinite(n2) && Math.abs(n1 - n2) < eps;
  const hasV = Number.isFinite(nv);
  const sameAll = same12 && (!hasV || Math.abs(n1 - nv) < eps);
  if (sameAll) return displayThroatAMm({ ...w, fillet_a_prov_mm: n1 });
  const parts = [`d1: ${n1.toFixed(2)}`];
  if (!same12 && Number.isFinite(n2)) parts.push(`d2: ${n2.toFixed(2)}`);
  if (hasV && Math.abs(n1 - nv) >= eps) parts.push(`v: ${nv.toFixed(2)}`);
  const minA = Math.min(
    n1,
    ...(Number.isFinite(n2) ? [n2] : []),
    ...(hasV ? [nv] : []),
  );
  const clamped = minA < FILLET_STRUCT_MIN_MM - 1e-9;
  return { text: parts.join(', '), clamped };
}

function visibleFilletWarnings(w, throatClamped) {
  const arr = Array.isArray(w.fillet_a_warnings) ? w.fillet_a_warnings : [];
  if (!throatClamped) return arr;
  return arr.filter(
    (s) =>
      typeof s !== 'string' ||
      (!s.includes('Throat a_prov') && !s.includes('structural shop minimum')),
  );
}

function jointCheckNodeLabel(c) {
  if (c.chord_end_node != null && Number.isFinite(Number(c.chord_end_node))) return String(c.chord_end_node);
  const mk = c.member_key || '';
  const m = mk.match(/_joint_(\d+)$/);
  return m ? m[1] : mk || '—';
}

function aggregateJointChecksByChordNode(jointChecks) {
  const byNode = new Map();
  for (const c of jointChecks) {
    const nodeKey = jointCheckNodeLabel(c);
    const u = Number(c.utilization);
    if (!Number.isFinite(u)) continue;
    const prev = byNode.get(nodeKey);
    if (!prev || u > Number(prev.utilization || 0)) {
      byNode.set(nodeKey, c);
    }
  }
  return [...byNode.entries()]
    .sort((a, b) => {
      const na = Number(a[0]);
      const nb = Number(b[0]);
      if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
      return String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true });
    })
    .map(([, c]) => c);
}

function sectionRef(s) {
  if (!s || !s.type || !s.id) return '—';
  return `${s.type} #${s.id}`;
}

function buildInputSummaryRows(inputs) {
  if (!inputs || typeof inputs !== 'object') return '<p class="muted">No input payload.</p>';
  const i = inputs;
  const rows = [];
  const add = (k, v) => {
    rows.push(`<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(v == null ? '—' : String(v))}</td></tr>`);
  };
  add('Shape', i.shape);
  add('Pattern', i.pattern);
  add('Structure type', i.structure_type);
  add('Span (m)', fmtNum(i.span_m, 3));
  add('Heights left / right (m)', `${fmtNum(i.height_left_m, 3)} / ${fmtNum(i.height_right_m, 3)}`);
  if (i.shape === 'dual') {
    add('Ridge height (m)', fmtNum(i.dual_ridge_height_m, 3));
    add('Ridge offset (m)', fmtNum(i.ridge_offset_m, 3));
  }
  add('Roller on', i.roller_on);
  add('Support placement', i.support_placement);
  add('Bearing connection offset (m)', fmtNum(i.bearing_connection_offset_m, 3));
  add('Eccentricity (mm)', i.eccentricity_mm);
  add('Chord family', i.chord_family);
  if (i.open_chord_shape) add('Open chord shape', i.open_chord_shape);
  add('Chord bracing lateral', i.chord_bracing_lateral_m != null ? String(i.chord_bracing_lateral_m) : '—');
  add('Chord bracing in-plane', i.chord_bracing_truss_plane_m != null ? String(i.chord_bracing_truss_plane_m) : '—');
  add('Tube width match (mm)', i.tube_width_match_mm);
  add('Manual sections', i.manual_sections ? 'Yes' : 'No');
  if (i.manual_sections) {
    add('Top chord', sectionRef(i.top_chord));
    if (i.shape === 'dual') {
      add('Top chord left', sectionRef(i.top_chord_left));
      add('Top chord right', sectionRef(i.top_chord_right));
    }
    add('Bottom chord', sectionRef(i.bottom_chord));
    add('Diagonal 1', sectionRef(i.diagonal));
    if (Number(i.diagonal_group_count) >= 2) add('Diagonal 2', sectionRef(i.diagonal_2));
    if (Number(i.diagonal_group_count) >= 3) add('Diagonal 3', sectionRef(i.diagonal_3));
    add('Vertical', i.vertical ? sectionRef(i.vertical) : '—');
  }
  if (i.structure_type === 'portal' && i.column) {
    add('Column', sectionRef(i.column));
    add('Column left / right (m)', `${fmtNum(i.column_left_m)} / ${fmtNum(i.column_right_m)}`);
    if (i.column_base_rotational_stiffness_kNm_per_rad != null)
      add('Column base rotational stiffness (kN·m/rad)', String(i.column_base_rotational_stiffness_kNm_per_rad));
  }
  add('Permanent load g (kN/m)', fmtNum(i.g_kN_m, 3));
  if (i.building_mode) {
    add('Permanent load g (kN/m²)', fmtNum(i.g_kN_m2, 3));
    add('Frame spacing (m)', fmtNum(i.frame_spacing_m, 2));
  }
  add('Snow (kN/m)', fmtNum(i.snow_kN_m, 3));
  if (i.sk_kN_m2 != null && String(i.sk_kN_m2).trim() !== '') add('Ground snow s_k (kN/m²)', fmtNum(i.sk_kN_m2, 3));
  if (i.snow_Ce != null && String(i.snow_Ce).trim() !== '') add('Snow C_e', fmtNum(i.snow_Ce, 2));
  if (i.snow_Ct != null && String(i.snow_Ct).trim() !== '') add('Snow C_t', fmtNum(i.snow_Ct, 2));
  add('Imposed (kN/m)', fmtNum(i.imposed_kN_m, 3));
  add('Wind (kN/m)', fmtNum(i.wind_kN_m, 3));
  if (i.wind_speed_ms != null && String(i.wind_speed_ms).trim() !== '') add('Wind v_b,0 (m/s)', fmtNum(i.wind_speed_ms, 2));
  if (i.terrain_category) add('Terrain category', String(i.terrain_category));
  if (i.wind_model_en1991) add('Wind model', 'EN 1991-1-4');
  if (i.snow_model_en1991) add('Snow model', 'EN 1991-1-3');
  add('Snow checkerboard ULS', i.snow_checkerboard_ul ? 'Yes' : 'No');
  add('Bottom chord line load (kN/m)', fmtNum(i.bottom_chord_kN_m, 3));
  add('Self-weight', i.self_weight ? 'Yes' : 'No');
  add('Optimize / auto-design', i.optimize ? 'Yes' : 'No');
  add('Utilization target', fmtNum(i.utilization_target, 2));
  add('Steel cost (EUR/kg)', fmtNum(i.cost_eur_per_installed_steel_kg, 2));
  add('Overlap joint cost (EUR)', fmtNum(i.cost_eur_per_overlap_joint, 2));
  add('Deflection limit (span/L)', String(i.deflection_limit_ratio));
  add('National annex', i.national_annex);
  add('Chord imperfection', i.chord_imperfection_include ? `On (span/${i.chord_imperfection_span_ratio || 500})` : 'Off');
  add('Portal sway imperfection', i.portal_include_sway_imperfection ? 'On' : 'Off');
  add('Joint debug plots', i.joint_debug_plots !== false ? 'On' : 'Off');
  add('Overlap hidden seam welded', i.overlap_hidden_seam_welded ? 'Yes' : 'No');
  return `<table class="report kv-table"><tbody>${rows.join('')}</tbody></table>`;
}

function buildAlertsHtml(results) {
  let h = '';
  if (results.solver_note) {
    h += `<p><strong>Solver</strong>: ${escapeHtml(results.solver_note)}</p>`;
  }
  if (results.snow_checkerboard_ul?.enabled) {
    h += `<p><strong>Snow checkerboard (ULS)</strong>: ${escapeHtml(results.snow_checkerboard_ul.note || '')} Variants: ${escapeHtml(
      (results.snow_checkerboard_ul.variants_enveloped || []).join(', '),
    )}</p>`;
  }
  if (results.auto_design) {
    const ad = results.auto_design;
    let t = `Panels = ${ad.selected_panel_count}`;
    if (ad.open_chord_shape != null) t += ` · open profile ${ad.open_chord_shape}`;
    if (ad.estimated_steel_kg != null) t += ` · ≈ ${Number(ad.estimated_steel_kg).toFixed(0)} kg steel (members)`;
    if (ad.optimization_cost_eur) {
      const oc = ad.optimization_cost_eur;
      t += ` · objective ≈ ${Number(oc.total_objective_eur).toFixed(0)} EUR`;
    }
    h += `<p><strong>Auto design</strong>: ${escapeHtml(t)}</p>`;
  }
  if (results.column_auto_design?.selected_column) {
    const c = results.column_auto_design.selected_column;
    h += `<p><strong>Auto column</strong>: ${escapeHtml(c.name || c.type || '')}</p>`;
    if (results.column_auto_design.column_weak_axis_brace_proposed_m != null) {
      h += `<p class="muted">Suggested weak-axis brace interval: ${Number(
        results.column_auto_design.column_weak_axis_brace_proposed_m,
      ).toFixed(2)} m${results.column_auto_design.weak_axis_non_governing ? ' (strong-axis governing)' : ''}</p>`;
    }
    if (results.column_auto_design.deflection_note) {
      h += `<p class="muted">${escapeHtml(results.column_auto_design.deflection_note)}</p>`;
    }
  }
  const g = results.geometry;
  if (g?.support_on_top) {
    h += `<p class="muted">Supports on top chord${g.bearing_connection_offset_m > 0 ? ` · offset ${Number(g.bearing_connection_offset_m).toFixed(2)} m` : ''}</p>`;
  }
  if (results.chord_bracing) {
    const cb = results.chord_bracing;
    h += `<p class="muted">Chord buckling lengths: in-plane ${Number(cb.L_plane_m || 0).toFixed(3)} m, lateral ${Number(cb.L_lateral_m || 0).toFixed(3)} m (panel ${Number(cb.L_panel_m || 0).toFixed(3)} m).${cb.notation_note ? ` ${cb.notation_note}` : ''}</p>`;
  }
  return h || '<p class="muted">No extra notes.</p>';
}

function buildSummaryTabHtml(results, inputs) {
  const profileRows = profilesSummary(results.selected_profiles, results.geometry);
  const pr = profileRows
    .map((r) => `<tr><td>${escapeHtml(r.label)}</td><td>${escapeHtml(r.value)}</td></tr>`)
    .join('');
  const ci = results.chord_imperfection || results.fem_imperfection;
  let imperf = '';
  if (ci) {
    imperf =
      ci.included === false
        ? '<p>Chord imperfection: off</p>'
        : `<p>Chord imperfection: span / ${Number(ci.span_ratio || 500)} → ${Number(ci.amplitude_mid_mm || 0).toFixed(2)} mm at midspan (top + bottom chords).</p>`;
  }
  let portal = '';
  if (results.portal_sway_imperfection) {
    const p = results.portal_sway_imperfection;
    portal = p.applied
      ? `<p>Portal sway (ULS): φ = ${Number(p.phi || 0).toFixed(5)}, H = ${(Number(p.H_N || 0) / 1000).toFixed(2)} kN (V_Ed = ${(Number(p.V_ed_N || 0) / 1000).toFixed(2)} kN)</p>`
      : `<p>Portal sway (ULS): ${escapeHtml(p.note || 'Not applied')}</p>`;
  }
  let je = '';
  if (results.joint_envelope) {
    const j = results.joint_envelope;
    je = `<p><strong>Governing joint (ULS envelope)</strong>: node ${escapeHtml(jointCheckNodeLabel(j))} — ${escapeHtml(j.mode || '—')}${
      j.governing_load_variant ? ` (${escapeHtml(j.governing_load_variant)})` : ''
    }, util. ${escapeHtml(utilPercent(j.utilization))}${
      j.N_Rd_kN != null && Number.isFinite(Number(j.N_Rd_kN)) ? `, N_Rd = ${Number(j.N_Rd_kN).toFixed(1)} kN` : ''
    }.</p>`;
  }
  const defl = results.deflection || {};
  const bowOn = !!(results.chord_imperfection && results.chord_imperfection.included);
  const bowHtml = bowOn
    ? `<p>Imperfection bow: ${Number(results.chord_imperfection.amplitude_mid_mm || 0).toFixed(2)} mm</p>`
    : '';
  const deflHtml = `<p>Max bottom-chord vertical displacement (uz): ${Number(defl.uz_max_mm || defl.uy_max_mm || 0).toFixed(2)} mm (limit ${Number(defl.limit_mm || 0).toFixed(1)} mm)</p>
    ${bowHtml}
    <p><strong>Suggested precamber</strong> (fabrication): ${Number(defl.suggested_precamber_mm || 0).toFixed(2)} mm</p>
    ${!defl.ok ? '<p class="muted"><strong>Warning:</strong> deflection exceeds serviceability limit.</p>' : ''}`;
  const governing = formatGoverningCombination(results, inputs);
  const governingHtml = governing
    ? `<h3>Governing load combination</h3><p>${escapeHtml(governing)}</p>`
    : '';
  return `${governingHtml}<h3>Chosen profiles</h3><table class="report"><tbody>${pr}</tbody></table>${imperf}${portal}${je}<h3>Deflection & precamber (SLS characteristic)</h3>${deflHtml}`;
}

function aggregateMemberChecksForDisplay(memberChecks) {
  const hasSplitTop = memberChecks.some((c) => c.group === 'top_chord_left' || c.group === 'top_chord_right');
  const byBucket = new Map();
  const bucketFor = (c) => {
    const g = c.group || '';
    if (g === 'bottom_chord') return 'bottom_chord';
    if (g === 'top_chord_left') return 'top_chord_left';
    if (g === 'top_chord_right') return 'top_chord_right';
    if (g === 'top_chord') return 'top_chord';
    return null;
  };
  for (const c of memberChecks) {
    const bucket = bucketFor(c);
    if (!bucket) {
      const key = `other:${c.group || ''}:${c.member_key || ''}:${c.mode || ''}`;
      const u = Number(c.utilization);
      const prev = byBucket.get(key);
      if (!prev || (Number.isFinite(u) && u > Number(prev.utilization || 0))) {
        byBucket.set(key, { ...c });
      }
      continue;
    }
    if (hasSplitTop && c.group === 'top_chord') continue;
    const u = Number(c.utilization);
    if (!Number.isFinite(u)) continue;
    const prev = byBucket.get(bucket);
    if (!prev || u > Number(prev.utilization || 0)) {
      byBucket.set(bucket, { ...c, group: bucket, member_key: c.member_key });
    }
  }
  const chordOrder = ['top_chord', 'top_chord_left', 'top_chord_right', 'bottom_chord'];
  const chords = chordOrder.map((b) => byBucket.get(b)).filter(Boolean);
  for (const b of chordOrder) byBucket.delete(b);
  const others = [...byBucket.values()].sort((a, b) => {
    const ga = String(a.group || '');
    const gb = String(b.group || '');
    if (ga !== gb) return ga.localeCompare(gb);
    return String(a.member_key || '').localeCompare(String(b.member_key || ''), undefined, { numeric: true });
  });
  return [...chords, ...others];
}

function buildMemberChecksHtml(results) {
  const checks = results.design_checks || [];
  const memberChecks = aggregateMemberChecksForDisplay(checks.filter((c) => c.group !== 'joint'));
  const showChordMomentCol = memberChecks.some(
    (c) => c.M_combined_kNm != null && Number.isFinite(Number(c.M_combined_kNm)),
  );
  let head = '<tr><th>Group</th><th>Key</th><th>Section</th><th>Mode</th>';
  if (showChordMomentCol) head += '<th class="num">M (kN·m)</th>';
  head += '<th class="num">Util.</th></tr>';
  const body = memberChecks
    .map((c) => {
      let mcell = '—';
      if (showChordMomentCol) {
        if (c.M_combined_kNm != null && Number.isFinite(Number(c.M_combined_kNm))) {
          mcell = `${Number(c.M_combined_kNm).toFixed(2)} <span class="muted">(FE ${Number(c.M_fem_kNm || 0).toFixed(2)} + conn ${Number(c.M_connection_kNm || 0).toFixed(2)})</span>`;
        }
      }
      const sec = escapeHtml(c.section_display || '—');
      const combo =
        c.governing_load_variant != null && c.governing_load_variant !== ''
          ? `<span class="muted"><br>${escapeHtml(formatLoadVariantLabel(c.governing_load_variant))}</span>`
          : '';
      let row = `<tr><td>${escapeHtml(c.group)}</td><td>${escapeHtml(c.member_key || '')}</td><td>${sec}</td>`;
      row += `<td>${escapeHtml(c.mode || '')}${combo}</td>`;
      if (showChordMomentCol) row += `<td class="num">${mcell}</td>`;
      row += `<td class="num">${escapeHtml(utilPercent(c.utilization))}</td></tr>`;
      return row;
    })
    .join('');
  if (!memberChecks.length) return '<p class="muted">No member checks.</p>';
  return `<p class="muted">Chord lines: governing segment only (max UR). Dual-pitch keeps left/right top chords.</p>
    <table class="report allow-row-split"><thead>${head}</thead><tbody>${body}</tbody></table>`;
}

function buildExpandedWeldRows(weldNotes) {
  return (weldNotes || []).map((w) => ({
    w,
    nodeLabel: w.joint_node_id != null ? String(w.joint_node_id) : '—',
    topSuffix: '',
  }));
}

function buildJointsTabHtml(results) {
  const checks = results.design_checks || [];
  const jointChecks = checks.filter((c) => c.group === 'joint');
  const jointChecksTable = aggregateJointChecksByChordNode(jointChecks);
  const showJointLoadVariantCol = jointChecks.some((c) => c.governing_load_variant);
  let h = '';
  if (!jointChecksTable.length) {
    h += '<p class="muted">No joint checks in this model.</p>';
  } else {
    h +=
      '<p class="muted">EN 1993-1-8 tubular checks per diagonal; rows show governing case per chord node (max utilization).</p>';
    let jhead = '<tr><th>Node</th>';
    if (showJointLoadVariantCol) jhead += '<th>ULS load</th>';
    jhead += '<th>Mode</th><th class="num">Util.</th></tr>';
    const jbody = jointChecksTable
      .map((c) => {
        let r = `<tr><td>${escapeHtml(jointCheckNodeLabel(c))}</td>`;
        if (showJointLoadVariantCol) r += `<td>${escapeHtml(c.governing_load_variant || '—')}</td>`;
        r += `<td>${escapeHtml(c.mode || '')}</td><td class="num">${escapeHtml(utilPercent(c.utilization))}</td></tr>`;
        return r;
      })
      .join('');
    h += `<table class="report allow-row-split"><thead>${jhead}</thead><tbody>${jbody}</tbody></table>`;
  }
  const weldNotes = results.weld_notes || [];
  if (weldNotes.length) {
    h += '<h3>K-joint / weld rows</h3>';
    const whead =
      '<tr><th>Node</th><th class="num">Brace 1 θ (°)</th><th class="num">Brace 2 θ (°)</th><th>Chord face gap / overlap</th><th class="num">L_eff (m)</th><th class="num">Throat a (mm)</th><th>Warnings</th></tr>';
    const wbody = buildExpandedWeldRows(weldNotes)
      .map(({ w, nodeLabel, topSuffix }) => {
        const { text: throatText, clamped: throatClamped } = formatWeldThroatColumnMm(w);
        const warnShown = visibleFilletWarnings(w, throatClamped);
        const warn = warnShown.length
          ? warnShown.join(' ')
          : w.central_placeholder
            ? 'Centre placeholder'
            : '—';
        const t1 =
          Array.isArray(w.theta_brace_deg) && w.theta_brace_deg.length >= 1 ? Number(w.theta_brace_deg[0]).toFixed(1) : '—';
        const t2 =
          Array.isArray(w.theta_brace_deg) && w.theta_brace_deg.length >= 2 ? Number(w.theta_brace_deg[1]).toFixed(1) : '—';
        const legSuf = w.chord_leg === 'top' ? ' (top chord)' : '';
        return `<tr><td>${escapeHtml(nodeLabel)}${escapeHtml(topSuffix)}${escapeHtml(legSuf)}</td><td class="num">${t1}</td><td class="num">${t2}</td><td>${escapeHtml(
          formatWeldChordGapMm(w),
        )}</td><td class="num">${escapeHtml(formatWeldLeffColumnM(w))}</td><td class="num">${escapeHtml(
          throatText,
        )}</td><td>${escapeHtml(warn)}</td></tr>`;
      })
      .join('');
    h += `<table class="report allow-row-split"><thead>${whead}</thead><tbody>${wbody}</tbody></table>`;
  }
  const ecc = results.weld_eccentricities || [];
  if (ecc.length) {
    h += '<h3>Weld eccentricities</h3>';
    ecc.forEach((w) => {
      h += `<p>Bottom node ${escapeHtml(String(w.bottom_node_id))}: e = ${(Number(w.e_m) * 1000).toFixed(1)} mm — included in chord M where applicable.</p>`;
    });
  }
  if (!jointChecksTable.length && !weldNotes.length && !ecc.length) {
    h += '<p class="muted">No weld gap or offset notes.</p>';
  }
  return h;
}

function buildForcesHtml(results) {
  const sol = results.solution || {};
  const chordGroups = new Set(['bottom_chord', 'top_chord', 'top_chord_left', 'top_chord_right']);
  const trussRows = (sol.truss_forces || [])
    .map(
      (f) =>
        `<tr><td>${f.i}</td><td>${f.j}</td><td>${escapeHtml(f.group)}</td><td class="num">${Number(f.N_kN).toFixed(2)}</td></tr>`,
    )
    .join('');
  const chordRows = (sol.frame_forces || [])
    .filter((f) => chordGroups.has(f.group))
    .map(
      (f) =>
        `<tr><td>${f.i}</td><td>${f.j}</td><td>${escapeHtml(f.group)} (beam)</td><td class="num">${Number(f.N_i_kN).toFixed(2)}</td></tr>`,
    )
    .join('');
  const inner = trussRows + chordRows;
  if (!inner) return '<p class="muted">No force output.</p>';
  return `<p class="muted">Chords are continuous beam elements; axial N at first node of each segment (full V, M in analysis payload).</p>
    <table class="report allow-row-split"><thead><tr><th>i</th><th>j</th><th>Group</th><th class="num">N (kN, tension +)</th></tr></thead><tbody>${inner}</tbody></table>`;
}

function buildReactionsHtml(results) {
  const envelope = Array.isArray(results.reaction_envelope) ? results.reaction_envelope : [];
  if (envelope.length) {
    const rows = envelope
      .map(
        (r) =>
          `<tr><td>${escapeHtml(String(r.node))}</td><td>${escapeHtml(String(r.dof))}</td><td class="num" title="${escapeHtml(String(r.min_combo || ''))}">${Number(r.min).toFixed(2)}</td><td class="num" title="${escapeHtml(String(r.max_combo || ''))}">${Number(r.max).toFixed(2)}</td></tr>`,
      )
      .join('');
    return `<p class="muted">Algebraic minimum and maximum of each support reaction over every ULS combination.</p>
      <table class="report allow-row-split"><thead><tr><th>Node</th><th>DOF</th><th class="num">Min</th><th class="num">Max</th></tr></thead><tbody>${rows}</tbody></table>`;
  }
  const sol = results.solution || {};
  const rows = (sol.reactions || [])
    .map((r) => `<tr><td>${escapeHtml(String(r.node))}</td><td>${escapeHtml(String(r.dof))}</td><td class="num">${Number(r.value).toFixed(2)}</td></tr>`)
    .join('');
  if (!rows) return '<p class="muted">No reactions.</p>';
  return `<table class="report allow-row-split"><thead><tr><th>Node</th><th>DOF</th><th class="num">Value</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function buildDeflectionTabHtml(results) {
  const defl = results.deflection || {};
  return `<p>Max bottom-chord vertical displacement (uz): ${Number(defl.uz_max_mm || defl.uy_max_mm || 0).toFixed(2)} mm (limit ${Number(defl.limit_mm || 0).toFixed(1)} mm). Suggested precamber: ${Number(defl.suggested_precamber_mm || 0).toFixed(2)} mm.</p>${
    !defl.ok ? '<p class="muted"><strong>Warning:</strong> deflection exceeds limit.</p>' : ''
  }<p class="muted">Deflected shape figure: use the on-screen Deflection tab in the app (SVG).</p>`;
}

function buildLoadingsTabHtml(results, inputs) {
  const geom = results.geometry;
  const { cases, windMeta, snowMeta } = resolveLoadingCases(results, inputs);
  if (!cases.length) {
    return '<p class="muted">No characteristic EN snow/wind load data on this result.</p>';
  }
  let h = '';
  const basisRows = buildBasisRows(results, inputs, windMeta, snowMeta);
  if (basisRows.length) {
    h += '<h3>Basis</h3><table class="report kv-table"><tbody>';
    basisRows.forEach((r) => {
      h += `<tr><td>${escapeHtml(r.label)}</td><td>${escapeHtml(String(r.value))}</td></tr>`;
    });
    h += '</tbody></table>';
  }
  cases.forEach((loadCase) => {
    h += `<h3>${escapeHtml(loadCase.label)}</h3>`;
    if (geom) {
      h += `<div class="loadings-fig">${buildLoadingsSvgString(geom, loadCase)}</div>`;
    }
    if (loadCase.kind === 'wind') {
      const zoneRows = buildWindZonesTableRows(loadCase, windMeta);
      if (zoneRows.length) {
        h +=
          '<h4>Relevant wind zones</h4><table class="report allow-row-split"><thead><tr><th>Zone</th><th>Surface</th><th>Extent</th><th class="num">c_pe</th><th class="num">Length</th></tr></thead><tbody>';
        zoneRows.forEach((r) => {
          h += `<tr><td>${escapeHtml(String(r.zone))}</td><td>${escapeHtml(String(r.surface))}</td><td>${escapeHtml(String(r.extent))}</td><td class="num">${r.cpe}</td><td class="num">${r.length}</td></tr>`;
        });
        h += '</tbody></table>';
      }
    }
    const windRows = loadCase.kind === 'wind' ? buildWindCaseRows(loadCase) : [];
    if (windRows.length) {
      h += '<table class="report kv-table"><tbody>';
      windRows.forEach((r) => {
        h += `<tr><td>${escapeHtml(r.label)}</td><td>${escapeHtml(String(r.value))}</td></tr>`;
      });
      h += '</tbody></table>';
    }
    const roofZoneRows = loadCase.kind === 'wind' ? buildRoofZoneSegmentRows(loadCase) : [];
    if (roofZoneRows.length) {
      h +=
        '<h4>Roof zones per segment</h4><table class="report allow-row-split"><thead><tr><th>Seg</th><th>x₀</th><th>x₁</th><th>Zones</th><th class="num">c_pe,net</th><th class="num">w</th></tr></thead><tbody>';
      roofZoneRows.forEach((r) => {
        h += `<tr><td>${r.seg}</td><td>${r.x0}</td><td>${r.x1}</td><td>${escapeHtml(String(r.zones))}</td><td class="num">${r.cpeNet}</td><td class="num">${r.w}</td></tr>`;
      });
      h += '</tbody></table>';
    }
    const frameRows = loadCase.kind === 'wind' ? buildFrameZoneRows(loadCase) : [];
    if (frameRows.length) {
      h +=
        '<h4>Per-frame zones</h4><table class="report allow-row-split"><thead><tr><th>Frame</th><th>Wall zones</th><th>Roof zones</th><th class="num">q_L</th><th class="num">q_R</th></tr></thead><tbody>';
      frameRows.forEach((r) => {
        h += `<tr><td>${r.frame}</td><td>${escapeHtml(String(r.wallZones))}</td><td>${escapeHtml(String(r.roofZones))}</td><td class="num">${r.qLeft}</td><td class="num">${r.qRight}</td></tr>`;
      });
      h += '</tbody></table>';
    }
    const snowHasShape =
      (loadCase.id === 'snow' || loadCase.id === 'snow_case_ii') &&
      Array.isArray(loadCase.detail?.mu_per_seg) &&
      loadCase.detail.mu_per_seg.length;
    if (loadCase.kind === 'bottom') {
      const bottomRows = buildBottomSegmentRows(loadCase, geom);
      if (bottomRows.length) {
        h +=
          '<table class="report allow-row-split"><thead><tr><th>Seg</th><th>x₀ (m)</th><th>x₁ (m)</th><th class="num">w (kN/m)</th></tr></thead><tbody>';
        bottomRows.forEach((r) => {
          h += `<tr><td>${r.seg}</td><td>${r.x0}</td><td>${r.x1}</td><td class="num">${r.w}</td></tr>`;
        });
        h += '</tbody></table>';
      }
    } else if (snowHasShape) {
      const snowRows = buildSnowSegmentRows(loadCase, geom);
      if (snowRows.length) {
        h +=
          '<table class="report allow-row-split"><thead><tr><th>Seg</th><th>x₀ (m)</th><th>x₁ (m)</th><th>Side</th><th class="num">μ</th><th class="num">s (kN/m²)</th><th class="num">w (kN/m)</th></tr></thead><tbody>';
        snowRows.forEach((r) => {
          h += `<tr><td>${r.seg}</td><td>${r.x0}</td><td>${r.x1}</td><td>${escapeHtml(String(r.side))}</td><td class="num">${r.mu}</td><td class="num">${r.s}</td><td class="num">${r.w}</td></tr>`;
        });
        h += '</tbody></table>';
      }
    } else if (!roofZoneRows.length) {
      const roofRows = buildRoofSegmentRows(loadCase, geom);
      if (roofRows.length) {
        h +=
          '<table class="report allow-row-split"><thead><tr><th>Seg</th><th>x₀ (m)</th><th>x₁ (m)</th><th>Zones</th><th class="num">w (kN/m)</th></tr></thead><tbody>';
        roofRows.forEach((r) => {
          h += `<tr><td>${r.seg}</td><td>${r.x0}</td><td>${r.x1}</td><td>${escapeHtml(String(r.zones || '—'))}</td><td class="num">${r.w}</td></tr>`;
        });
        h += '</tbody></table>';
      }
    }
  });
  return h;
}

/**
 * @param {{ geometry?: object, results: object, inputs: object, resultsOutOfDate?: boolean, generatedAt?: string }} opts
 */
export function buildTrussPrintHtml({ results, inputs, resultsOutOfDate, generatedAt } = {}) {
  if (!results) {
    return buildPrintDocumentHtml({ title: 'Truss analysis report', bodyHtml: '<p>No results.</p>' });
  }
  if (results.error) {
    return buildPrintDocumentHtml({
      title: 'Truss analysis report',
      bodyHtml: `<p class="muted">${escapeHtml(String(results.error))}</p>`,
    });
  }
  const combo = results.loads_combo || {};
  const governingText = formatGoverningCombination(results, inputs);
  const subParts = [generatedAt || new Date().toLocaleString()];
  if (resultsOutOfDate) subParts.push('Note: inputs may have changed since this analysis — recalculate to sync.');
  if (governingText || combo.name) subParts.push(`Governing combination: ${governingText || combo.name}`);
  const subtitle = subParts.join(' · ');

  let body = '';
  body += printSectionHtml('Input summary', buildInputSummaryRows(inputs), { allowBreak: true });
  body += printSectionHtml('Notes', buildAlertsHtml(results), { allowBreak: true });
  body += printSectionHtml('Summary', buildSummaryTabHtml(results, inputs));
  body += printSectionHtml('Member checks', buildMemberChecksHtml(results), { allowBreak: true, breakBefore: true });
  body += printSectionHtml('Joints', buildJointsTabHtml(results), { allowBreak: true });
  body += printSectionHtml('Forces', buildForcesHtml(results), { allowBreak: true, breakBefore: true });
  body += printSectionHtml('Reactions', buildReactionsHtml(results), { allowBreak: true });
  body += printSectionHtml('Deflection', buildDeflectionTabHtml(results));
  body += printSectionHtml('Loadings', buildLoadingsTabHtml(results, inputs), { allowBreak: true, breakBefore: true });

  return buildPrintDocumentHtml({
    title: 'Truss & frame (steel) — analysis report',
    subtitle,
    bodyHtml: body,
  });
}

export function openTrussPrintReport(opts) {
  const html = buildTrussPrintHtml({
    ...opts,
    generatedAt: opts.generatedAt || new Date().toLocaleString(),
  });
  openPrintHtmlDocument(html);
}
